/**
 * Brand utility functions for domain-based brand detection
 */

import { BRAND_CONSTANTS } from '@/lib/constants/appConstants';

/**
 * Extracts the brand name from a domain
 * @param domainName - The domain name (e.g., 'visible-sit1.com', 'totalwireless.com')
 * @returns The brand name (e.g., 'visible', 'totalwireless') or empty string if not found
 */
export const getBrandFromDomain = (domainName: string): string => {
  if (!domainName) return '';
  
  // Remove protocol if present
  const cleanDomain = domainName.replace(/^https?:\/\//, '').toLowerCase();
  
  // Check for specific brand domains
  if (cleanDomain.includes('visible')) {
    return 'VISIBLE';
  } else if (cleanDomain.includes('totalwireless') || cleanDomain.includes('total-wireless')) {
    return 'TOTALWIRELESS';
  } else if (cleanDomain.includes('straighttalk') || cleanDomain.includes('straight-talk')) {
    return 'STRAIGHTTALK';
  } else if (cleanDomain.includes('tracfone')) {
    return 'TRACFONE';
  } else if (cleanDomain.includes('simplemobile') || cleanDomain.includes('simple-mobile')) {
    return 'SIMPLEMOBILE';
  } else if (cleanDomain.includes('safelink')) {
    return 'SAFELINK';
  } else if (cleanDomain.includes('net10')) {
    return 'NET10';
  } else if (cleanDomain.includes('familymobile') || cleanDomain.includes('family-mobile')) {
    return 'FAMILYMOBILE';
  }
  
  return '';
};

/**
 * Determines the environment from the domain
 * @param domainName - The domain name
 * @returns Environment string: 'DIT1', 'SIT1', 'TEST', or 'PROD'
 */
export const getEnvironmentFromDomain = (domainName: string): string => {
  if (!domainName) return 'DIT1';
  
  const cleanDomain = domainName.toLowerCase();
  
  if (cleanDomain.includes('sit1')) {
    return 'SIT1';
  } else if (cleanDomain.includes('test') || cleanDomain.includes('stage') || cleanDomain.includes('sit2')) {
    return 'TEST';
  } else if (cleanDomain.includes('sita') || cleanDomain.includes('dit1') || cleanDomain.includes('localhost')) {
    return 'DIT1';
  }
  
  // Default to production
  return 'PROD';
};

/**
 * Validates and normalizes a brand name from subdomain string
 * @param subdomain - The subdomain or brand identifier
 * @returns A valid brand name or the default brand if invalid
 */
export const validateAndNormalizeBrand = (subdomain: unknown): string => {
  // Handle invalid input types
  if (!subdomain || typeof subdomain !== 'string') {
    return BRAND_CONSTANTS.DEFAULT_BRAND;
  }
  
  // Normalize: lowercase, trim, remove hyphens
  const normalized = subdomain.toLowerCase().trim().replaceAll('-', '');
  
  if (!normalized) {
    return BRAND_CONSTANTS.DEFAULT_BRAND;
  }
  
  // Check if it's already a valid brand
  if ((BRAND_CONSTANTS.VALID_SUBDOMAINS as readonly string[]).includes(normalized)) {
    return normalized;
  }
  
  // Handle alternative naming patterns
  if (normalized.includes('total')) return BRAND_CONSTANTS.BRANDS.TOTAL_WIRELESS;
  if (normalized.includes('straight')) return BRAND_CONSTANTS.BRANDS.STRAIGHT_TALK;
  if (normalized.includes('simple')) return BRAND_CONSTANTS.BRANDS.SIMPLE_MOBILE;
  if (normalized.includes('family')) return BRAND_CONSTANTS.BRANDS.FAMILY_MOBILE;
  
  // Default fallback for unknown brands
  return BRAND_CONSTANTS.DEFAULT_BRAND;
};

/**
 * Extracts and validates brand name from the current hostname
 * @returns A valid brand name or the default brand if invalid
 */
export const getBrandNameFromHostname = (): string => {
  // SSR check - window is not available during server-side rendering
  if (globalThis.window === undefined) {
    return BRAND_CONSTANTS.DEFAULT_BRAND;
  }

  const hostname = globalThis.window.location.hostname;
  
  // Handle localhost and development environments
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return BRAND_CONSTANTS.DEFAULT_BRAND;
  }
  
  // Extract main domain from hostname (e.g., "sita.straighttalk.com" -> "straighttalk.com")
  const parts = hostname?.split('.');
  const mainDomain = parts && parts.length > 2 
    ? parts.slice(1).join('.') // Multi-subdomain: take everything after first part
    : hostname; // Single domain: use as-is
  
  if (!mainDomain) {
    return BRAND_CONSTANTS.DEFAULT_BRAND;
  }
  
  const normalizedDomain = mainDomain.toLowerCase().trim();
  
  // Check if the normalized domain is a valid brand domain
  // if ((BRAND_CONSTANTS.VALID_SUBDOMAINS as readonly string[]).includes(normalizedDomain)) {
  //   return normalizedDomain;
  // }
  
  // Handle alternative naming patterns (for flexibility)
  if (normalizedDomain.includes('total')) return BRAND_CONSTANTS.BRANDS.TOTAL_WIRELESS;
  if (normalizedDomain.includes('straight')) return BRAND_CONSTANTS.BRANDS.STRAIGHT_TALK;
  if (normalizedDomain.includes('simple')) return BRAND_CONSTANTS.BRANDS.SIMPLE_MOBILE;
  if (normalizedDomain.includes('family')) return BRAND_CONSTANTS.BRANDS.FAMILY_MOBILE;
  if (normalizedDomain.includes('gosmart')) return BRAND_CONSTANTS.BRANDS.GO_SMART_MOBILE;
  if (normalizedDomain.includes('safelink')) return BRAND_CONSTANTS.BRANDS.SAFELINK;
  if (normalizedDomain.includes('net10')) return BRAND_CONSTANTS.BRANDS.NET10;
  if (normalizedDomain.includes('tracfone')) return BRAND_CONSTANTS.BRANDS.TRACFONE;
  if (normalizedDomain.includes('visible')) return BRAND_CONSTANTS.BRANDS.VISIBLE;
  
  // Default fallback for unknown domains
  return BRAND_CONSTANTS.DEFAULT_BRAND;
};

/**
 * Gets the display name for a brand
 * @param brand - The brand identifier
 * @returns The human-readable brand name
 */
export const getBrandDisplayName = (brand: string): string => {
  const brandNames: Record<string, string> = {
    tracfone: 'TracFone',
    totalwireless: 'Total Wireless',
    straighttalk: 'Straight Talk',
    simplemobile: 'Simple Mobile',
    net10: 'NET10',
    safelink: 'SafeLink',
    familymobile: 'Family Mobile',
    visible: 'Visible'
  };
  
  return brandNames[brand.toLowerCase()] || 'TracFone';
};

/**
 * Determines if the current URL is for the Spanish version of the site
 * @return boolean indicating if the URL is for Spanish version
 */
export const isSpanishURL = (): boolean => {
  // SSR check - return default during server-side rendering
  if (globalThis.window === undefined) {
    return false;
  }
  
  const pathname = globalThis.window.location.pathname;
  
  // Handle locales - check if the path starts with /es/ for Spanish version
  if (pathname.includes('/es/')) {
    return true;
  }

  // Default fallback for unknown domains
  return false;
};